'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Phone, Mail, MapPin, Send } from 'lucide-react';
import { toast } from 'sonner';
import { SITE_CONFIG, WHATSAPP_URL } from '@/lib/config';

interface ContactForm {
  name: string;
  email: string;
  phone: string;
  message: string;
}

export default function ContactSection() {
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm<ContactForm>();

  const onSubmit = async (data: ContactForm) => {
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 1200));
    setSubmitting(false);
    toast.success('Message sent! We\'ll get back to you within 24 hours.');
    reset();
  };

  return (
    <section className="py-28 bg-[#F9F7F4]" id="contact">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <p className="section-label mb-3">Get in Touch</p>
          <div className="w-10 h-px mx-auto mb-6" style={{ background: '#C9A96E' }} />
          <h2
            className="text-[#0B1628] mb-5"
            style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 'clamp(1.75rem, 3vw, 2.75rem)',
              fontWeight: 400,
              lineHeight: 1.15,
              letterSpacing: '-0.01em',
            }}
          >
            We&apos;d Love to Hear<br />
            <span style={{ color: '#C9A96E' }}>From You</span>
          </h2>
          <p className="text-[#6B6560] max-w-md mx-auto leading-relaxed text-sm">
            Have questions about availability, pricing or our property management services? Reach out and we&apos;ll respond promptly.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
          {/* Contact info */}
          <div>
            <div className="flex flex-col gap-4 mb-10">
              <a
                href={`tel:${SITE_CONFIG.phone.replace(/\s/g, '')}`}
                className="flex items-center gap-5 p-5 bg-white hover:bg-[#FDFCFA] transition-colors group"
                style={{ border: '1px solid #E2DDD6', borderRadius: '2px' }}
              >
                <div
                  className="w-11 h-11 flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(201,169,110,0.08)', border: '1px solid rgba(201,169,110,0.25)', borderRadius: '2px' }}
                >
                  <Phone size={17} className="text-[#C9A96E]" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#6B6560] tracking-widest uppercase mb-0.5" style={{ letterSpacing: '0.12em' }}>Phone / WhatsApp</p>
                  <p className="font-semibold text-[#0B1628]">{SITE_CONFIG.phone}</p>
                </div>
              </a>

              <a
                href={`mailto:${SITE_CONFIG.email}`}
                className="flex items-center gap-5 p-5 bg-white hover:bg-[#FDFCFA] transition-colors group"
                style={{ border: '1px solid #E2DDD6', borderRadius: '2px' }}
              >
                <div
                  className="w-11 h-11 flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(201,169,110,0.08)', border: '1px solid rgba(201,169,110,0.25)', borderRadius: '2px' }}
                >
                  <Mail size={17} className="text-[#C9A96E]" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#6B6560] tracking-widest uppercase mb-0.5" style={{ letterSpacing: '0.12em' }}>Email</p>
                  <p className="font-semibold text-[#0B1628]">{SITE_CONFIG.email}</p>
                </div>
              </a>

              <div
                className="flex items-center gap-5 p-5 bg-white"
                style={{ border: '1px solid #E2DDD6', borderRadius: '2px' }}
              >
                <div
                  className="w-11 h-11 flex items-center justify-center shrink-0"
                  style={{ background: 'rgba(201,169,110,0.08)', border: '1px solid rgba(201,169,110,0.25)', borderRadius: '2px' }}
                >
                  <MapPin size={17} className="text-[#C9A96E]" />
                </div>
                <div>
                  <p className="text-xs font-semibold text-[#6B6560] tracking-widest uppercase mb-0.5" style={{ letterSpacing: '0.12em' }}>Location</p>
                  <p className="font-semibold text-[#0B1628]">{SITE_CONFIG.location}</p>
                </div>
              </div>
            </div>

            {/* Social */}
            <div>
              <p className="text-xs font-semibold text-[#6B6560] tracking-widest uppercase mb-3" style={{ letterSpacing: '0.12em' }}>Follow Us</p>
              <div className="flex items-center gap-3 flex-wrap">
                <a
                  href={SITE_CONFIG.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 bg-white text-sm font-medium text-[#0B1628] hover:border-[#C9A96E] hover:text-[#C9A96E] transition-all"
                  style={{ border: '1px solid #E2DDD6', borderRadius: '2px' }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
                  Instagram
                </a>
                <a
                  href={SITE_CONFIG.tiktok}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 bg-white text-sm font-medium text-[#0B1628] hover:border-[#C9A96E] hover:text-[#C9A96E] transition-all"
                  style={{ border: '1px solid #E2DDD6', borderRadius: '2px' }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.33 6.33 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.18 8.18 0 0 0 4.77 1.52V6.75a4.85 4.85 0 0 1-1-.06z"/></svg>
                  TikTok
                </a>
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 bg-white text-sm font-medium text-[#0B1628] hover:border-[#25D366] hover:text-[#25D366] transition-all"
                  style={{ border: '1px solid #E2DDD6', borderRadius: '2px' }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                  WhatsApp
                </a>
              </div>
            </div>
          </div>

          {/* Contact form */}
          <div
            className="bg-white p-8 overflow-hidden"
            style={{ border: '1px solid #E2DDD6', borderRadius: '2px' }}
          >
            {/* Gold top accent */}
            <div className="h-px -mx-8 -mt-8 mb-7" style={{ background: 'linear-gradient(90deg, #C9A96E, transparent)' }} />
            <h3
              className="text-[#0B1628] mb-1 tracking-wide"
              style={{ fontSize: '1rem', fontWeight: 600, letterSpacing: '0.04em' }}
            >
              Send a Message
            </h3>
            <div className="w-8 h-px mb-6" style={{ background: '#C9A96E' }} />
            <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5">
              <div>
                <label className="block text-xs font-semibold text-[#6B6560] tracking-widest uppercase mb-2" style={{ letterSpacing: '0.12em' }}>Full Name</label>
                <input
                  type="text"
                  placeholder="e.g. Amina Juma"
                  className={`input-field ${errors.name ? 'border-red-400 focus:ring-red-400' : ''}`}
                  {...register('name', { required: 'Full name is required' })}
                />
                {errors.name && <p className="mt-1 text-xs text-[#9B2335]">{errors.name.message}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#6B6560] tracking-widest uppercase mb-2" style={{ letterSpacing: '0.12em' }}>Email Address</label>
                <input
                  type="email"
                  placeholder="e.g. amina@example.co.tz"
                  className={`input-field ${errors.email ? 'border-red-400 focus:ring-red-400' : ''}`}
                  {...register('email', {
                    required: 'Email is required',
                    pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email address' },
                  })}
                />
                {errors.email && <p className="mt-1 text-xs text-[#9B2335]">{errors.email.message}</p>}
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#6B6560] tracking-widest uppercase mb-2" style={{ letterSpacing: '0.12em' }}>Phone Number</label>
                <input
                  type="tel"
                  placeholder="e.g. 0712 345 678"
                  className="input-field"
                  {...register('phone')}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#6B6560] tracking-widest uppercase mb-2" style={{ letterSpacing: '0.12em' }}>Message</label>
                <textarea
                  rows={4}
                  placeholder="Tell us about your stay requirements, dates, or questions…"
                  className={`input-field resize-none ${errors.message ? 'border-red-400 focus:ring-red-400' : ''}`}
                  {...register('message', { required: 'Please include a message' })}
                />
                {errors.message && <p className="mt-1 text-xs text-[#9B2335]">{errors.message.message}</p>}
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 font-semibold py-3.5 text-xs tracking-widest uppercase transition-all btn-scale disabled:opacity-60 hover:brightness-110"
                style={{ background: '#0B1628', color: 'white', borderRadius: '2px', letterSpacing: '0.12em' }}
              >
                {submitting ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending…
                  </>
                ) : (
                  <>
                    <Send size={14} />
                    Send Message
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}